import './TransactionFeesTable.scss';
import React from 'react';
import { FormGroup, Col, Row } from 'reactstrap';
import { Field, Input } from '@availity/form';
import InfoTooltip from '../../../../../components/info-tooltip/info-tooltip';
import { MarketplacePercentField } from '../MarketplaceSettingsFields/MarketplacePercentField';
import { MarketplaceCurrencyField } from '../MarketplaceSettingsFields/MarketplaceCurrencyField';
import {
    FeeRefundStatus,
    FeeRefundStatuses,
} from '../../../../../slices/marketplace/pricing/models/fee-refund-status.enum';
import { TransactionFees } from '../../../../../slices/marketplace/pricing/models/transaction-fees.model';
import { MarketplacePricing } from '../../../../../slices/marketplace/pricing/models/marketplace-pricing.model';

interface TransactionFeesTableProps {
    brokerageType: 'discountBrokeragePricing' | 'fullServiceBrokeragePricing';
    transactionFees: TransactionFees;
    onChange: (newState: TransactionFees) => void;
    setFormFieldValue: (name: string, value: string) => void;
    formState: MarketplacePricing;
}

const TransactionFeesTable = (props: TransactionFeesTableProps) => {
    const labelFieldWidthMd = 2;
    const multiFieldWidthMd = 5;
    const refundableFieldWidthMd = 2;
    const creditableFieldWidthMd = 2;
    const isFullServiceBrokerage =
        props.brokerageType == 'fullServiceBrokeragePricing' ? true : false;
    const partiallyRefundableTooltipText =
        'The application fee will be partially refunded in each of the following circumstances: ' +
        '1. 50% refund if a completed process yeilds no LOIs, ' +
        '2. 25% refund if a completed process yeilds ONLY 1 LOI, ' +
        "3. Up to 50% refund, at VetValue's discretion, depending upon the circumstances, if the engagement application is denied by VetValue";

    const refundableField = (fieldId: keyof TransactionFees) => (
        <>
            <div
                className={`shorter-field ${
                    props.transactionFees[fieldId] == FeeRefundStatus.Partially
                        ? ''
                        : 'info-tooltip-offset'
                }`}>
                <Input
                    type="select"
                    name={`${props.brokerageType}.transactionFees.${fieldId}`}
                    value={props.transactionFees[fieldId]}
                    onChange={(event) => {
                        const numVal = Number(event.currentTarget.value);
                        const newState = {
                            ...props.transactionFees,
                        };
                        newState[fieldId] = numVal;
                        props.onChange(newState);
                    }}>
                    {FeeRefundStatuses.map((item) => (
                        <option
                            value={item.id}
                            key={props.brokerageType + fieldId + item.id}>
                            {item.name}
                        </option>
                    ))}{' '}
                </Input>
            </div>
            {props.transactionFees[fieldId] == FeeRefundStatus.Partially ? (
                <InfoTooltip
                    idText={`${props.brokerageType}.transactionFees.${fieldId}`}
                    text={partiallyRefundableTooltipText}
                />
            ) : (
                []
            )}
        </>
    );

    const creditableField = (fieldId: keyof TransactionFees) => (
        <div className="shorter-field">
            <MarketplacePercentField
                name={`${props.brokerageType}.transactionFees.${fieldId}`}
                value={
                    props.formState[props.brokerageType].transactionFees[
                        fieldId
                    ]
                }
                setValue={(name: string, value: string): void => {
                    props.setFormFieldValue(name, value);
                    const newState = {
                        ...props.transactionFees,
                    };
                    newState[fieldId] = Number(value.replace('%', ''));
                    props.onChange(newState);
                }}
            />
        </div>
    );

    const validateMonthsField = (event: any, keyprop: any, prefix: any) => {
        if (
            !/[0-9]/.test(event.key) &&
            event.key != 'Backspace' &&
            event.key != 'ArrowRight' &&
            event.key != 'ArrowLeft' &&
            event.key != 'Tab'
        ) {
            return true;
        }
        return false;
    };

    return (
        <div className="transaction-fees-table">
            <div className="fee-section">
                <FormGroup>
                    <div className="fee-section">
                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                <label>Fee Type</label>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="conditional-text-center">
                                <label>Fee Amount</label>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center aligned-md-2">
                                <label className="refundable-label">
                                    Refundable
                                </label>
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center aligned-md-2">
                                <label>Creditable</label>
                                <InfoTooltip
                                    idText={`${props.brokerageType}-creditable-header`}
                                    text={
                                        'Creditable against the transaction fee'
                                    }
                                />
                            </Col>
                        </Row>
                    </div>
                    <Row className="center-items fee-item-row margin-bottom-1-rem">
                        <Col md={labelFieldWidthMd}>
                            <label className="semibold-label">
                                Process Fees
                            </label>
                        </Col>
                    </Row>
                    <div className="fee-section">
                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                <div className="label-with-tooltip">
                                    <label>Buyer ID NDA Fee</label>
                                    <InfoTooltip
                                        idText={`${props.brokerageType}.transactionFees.idNdaFeePerBuyer`}
                                        text={
                                            "Once a buyer signs an ID NDA with the seller, the seller's indentity is disclosed to that buyer. " +
                                            'The seller selects which of the interested buyers it wishes to send an ID NDA. ' +
                                            'The seller is then charged ONLY for the number of buyers that actually elect to sign the ID NDA.'
                                        }
                                    />
                                </div>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="right-padding-5 multi-field">
                                <div className="shorter-field">
                                    <MarketplaceCurrencyField
                                        name={`${props.brokerageType}.transactionFees.idNdaFeePerBuyer`}
                                        value={
                                            props.formState[props.brokerageType]
                                                .transactionFees
                                                .idNdaFeePerBuyer
                                        }
                                        setFormValue={(
                                            name: string,
                                            value: string
                                        ): void => {
                                            props.setFormFieldValue(
                                                name,
                                                value
                                            );
                                            const newState = {
                                                ...props.transactionFees,
                                            };
                                            newState.idNdaFeePerBuyer =
                                                Number(value);
                                            props.onChange({
                                                ...newState,
                                            });
                                        }}
                                    />
                                </div>

                                <label className="label-right">
                                    * FTE DVMs per Buyer
                                </label>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                                {refundableField('idNdaFeeRefundable')}
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center flex-hor-center aligned-md-2">
                                {creditableField('idNdaFeeCreditable')}
                            </Col>
                        </Row>

                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                <div className="label-with-tooltip">
                                    <label>
                                        {isFullServiceBrokerage
                                            ? 'PIP'
                                            : 'PIQ'}{' '}
                                        Provision Fee
                                    </label>
                                    <InfoTooltip
                                        idText={`${props.brokerageType}.transactionFees.dataNdaFeePerBuyer`}
                                        text={
                                            `Buyers must sign a Data NDA before they are eligible to receive either an practice ${
                                                isFullServiceBrokerage
                                                    ? 'investment presentation ("PIP")'
                                                    : 'information questionnaire ("PIQ")'
                                            } or the detailed financial model. ` +
                                            'The seller selects which of the interested buyers it wishes to send a Data NDA. ' +
                                            'The seller is then charged ONLY for the number of buyers that actually elect to sign the Data NDA.'
                                        }
                                    />
                                </div>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="right-padding-5 multi-field">
                                <div className="shorter-field">
                                    <MarketplaceCurrencyField
                                        name={`${props.brokerageType}.transactionFees.dataNdaFeePerBuyer`}
                                        value={
                                            props.formState[props.brokerageType]
                                                .transactionFees
                                                .dataNdaFeePerBuyer
                                        }
                                        setFormValue={(
                                            name: string,
                                            value: string
                                        ): void => {
                                            props.setFormFieldValue(
                                                name,
                                                value
                                            );
                                            const newState = {
                                                ...props.transactionFees,
                                            };
                                            newState.dataNdaFeePerBuyer =
                                                Number(value);
                                            props.onChange({
                                                ...newState,
                                            });
                                        }}
                                    />
                                </div>

                                <label className="label-inner">
                                    * FTE DVMs per Buyer (first
                                </label>

                                <div className="double-digit-field">
                                    <Field
                                        type="number"
                                        name={`${props.brokerageType}.transactionFees.dataNdaFirstXCount`}
                                        onKeyDown={(event) => {
                                            if (event.key == '.') {
                                                event.preventDefault();
                                            }
                                        }}
                                        onChange={(event) => {
                                            let value = Number(
                                                event.target.value
                                            );
                                            const newState = {
                                                ...props.transactionFees,
                                            };
                                            newState.dataNdaFirstXCount = value;
                                            props.onChange({
                                                ...newState,
                                            });
                                        }}
                                    />
                                </div>

                                <label className="label-right">
                                    count as 1)
                                </label>
                            </Col>

                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                                {refundableField('dataNdaFeeRefundable')}
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center flex-hor-center aligned-md-2">
                                {creditableField('dataNdaFeeCreditable')}
                            </Col>
                        </Row>

                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                <div className="label-with-tooltip">
                                    <label>DFM Provision Fee</label>
                                    <InfoTooltip
                                        idText={`${props.brokerageType}.transactionFees.financialModelFeePerBuyer`}
                                        text={
                                            'The seller selects which of the interested buyers it wishes to allow to see the detailed financial model. ' +
                                            'The seller is then charged for each of these buyers which view the detailed financial model.'
                                        }
                                    />
                                </div>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="right-padding-5 multi-field">
                                <div className="shorter-field">
                                    <MarketplaceCurrencyField
                                        name={`${props.brokerageType}.transactionFees.financialModelFeePerBuyer`}
                                        value={
                                            props.formState[props.brokerageType]
                                                .transactionFees
                                                .financialModelFeePerBuyer
                                        }
                                        setFormValue={(
                                            name: string,
                                            value: string
                                        ): void => {
                                            props.setFormFieldValue(
                                                name,
                                                value
                                            );
                                            const newState = {
                                                ...props.transactionFees,
                                            };
                                            newState.financialModelFeePerBuyer =
                                                Number(value);
                                            props.onChange({
                                                ...newState,
                                            });
                                        }}
                                    />
                                </div>

                                <label className="label-inner">
                                    * FTE DVMs per Buyer (first
                                </label>

                                <div className="double-digit-field">
                                    <Field
                                        type="number"
                                        name={`${props.brokerageType}.transactionFees.financialModelFirstXCount`}
                                        value={
                                            props.transactionFees
                                                .financialModelFirstXCount
                                        }
                                        onKeyDown={(event) => {
                                            if (event.key == '.') {
                                                event.preventDefault();
                                            }
                                        }}
                                        onChange={(event) => {
                                            let value = Number(
                                                event.target.value
                                            );
                                            const newState = {
                                                ...props.transactionFees,
                                            };
                                            newState.financialModelFirstXCount =
                                                value;
                                            props.onChange({
                                                ...newState,
                                            });
                                        }}
                                    />
                                </div>

                                <label className="label-right">
                                    count as 1)
                                </label>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                                {refundableField('financialModelFeeRefundable')}
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center flex-hor-center aligned-md-2">
                                {creditableField('financialModelFeeCreditable')}
                            </Col>
                        </Row>

                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                {' '}
                                <div className="label-with-tooltip">
                                    <label>Buyer Meeting Credits</label>
                                    <InfoTooltip
                                        idText={`${props.brokerageType}.transactionFees.buyerMeetingCredit`}
                                        text={
                                            'Buyers are charged a refundable meeting fee for either an in-person or virtual meeting with the seller. ' +
                                            'This fee is refunded to the buyer if the seller is unable or unwilling to attend the meeting. ' +
                                            'Meetings are typically 3-5 hours in length and may include discussion over a meal. ' +
                                            "There is no meeting fee for the seller. VetValue credits 50% of the buyer's meeting fee towards the seller's transaction fee. "
                                        }
                                    />
                                </div>
                                <label>(NOT a Fee)</label>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="right-padding-5">
                                <Col className="multi-field left-padding-0 margin-bottom-half-rem">
                                    <div className="shorter-field">
                                        <MarketplaceCurrencyField
                                            name={`${props.brokerageType}.transactionFees.buyerMeetingVirtualCredit`}
                                            value={
                                                props.formState[
                                                    props.brokerageType
                                                ].transactionFees
                                                    .buyerMeetingVirtualCredit
                                            }
                                            setFormValue={(
                                                name: string,
                                                value: string
                                            ): void => {
                                                props.setFormFieldValue(
                                                    name,
                                                    value
                                                );
                                                const newState = {
                                                    ...props.transactionFees,
                                                };
                                                newState.buyerMeetingVirtualCredit =
                                                    Number(value);
                                                props.onChange({
                                                    ...newState,
                                                });
                                            }}
                                        />
                                    </div>

                                    <label className="label-right">
                                        credit per virtual meeting
                                    </label>
                                </Col>
                                <Col className="multi-field left-padding-0">
                                    <div className="shorter-field">
                                        <MarketplaceCurrencyField
                                            name={`${props.brokerageType}.transactionFees.buyerMeetingInPersonCredit`}
                                            value={
                                                props.formState[
                                                    props.brokerageType
                                                ].transactionFees
                                                    .buyerMeetingInPersonCredit
                                            }
                                            setFormValue={(
                                                name: string,
                                                value: string
                                            ): void => {
                                                props.setFormFieldValue(
                                                    name,
                                                    value
                                                );
                                                const newState = {
                                                    ...props.transactionFees,
                                                };
                                                newState.buyerMeetingInPersonCredit =
                                                    Number(value);
                                                props.onChange({
                                                    ...newState,
                                                });
                                            }}
                                        />
                                    </div>

                                    <label className="label-right">
                                        credit per in-person meeting
                                    </label>
                                </Col>
                            </Col>

                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                                {refundableField('buyerMeetingRefundable')}
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center flex-hor-center aligned-md-2">
                                {creditableField('buyerMeetingCreditable')}
                            </Col>
                        </Row>
                    </div>
                    <Row className="center-items fee-item-row margin-bottom-1-rem">
                        <Col md={labelFieldWidthMd}>
                            <label className="semibold-label">Other Fees</label>
                        </Col>
                    </Row>
                    <div className="fee-section">
                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                <div className="label-with-tooltip">
                                    <label>Application Fee</label>
                                    <InfoTooltip
                                        idText={`${props.brokerageType}.transactionFees.applicationFee`}
                                        text={
                                            'Charged at the time of application to engage this platform'
                                        }
                                    />
                                </div>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="right-padding-5 multi-field">
                                <div className="shorter-field">
                                    <MarketplaceCurrencyField
                                        name={`${props.brokerageType}.transactionFees.applicationFee`}
                                        value={
                                            props.formState[props.brokerageType]
                                                .transactionFees.applicationFee
                                        }
                                        setFormValue={(
                                            name: string,
                                            value: string
                                        ): void => {
                                            props.setFormFieldValue(
                                                name,
                                                value
                                            );
                                            const newState = {
                                                ...props.transactionFees,
                                            };
                                            newState.applicationFee =
                                                Number(value);
                                            props.onChange({
                                                ...newState,
                                            });
                                        }}
                                    />
                                </div>

                                <label className="label-inner">+</label>

                                <div className="shorter-field">
                                    <MarketplaceCurrencyField
                                        name={`${props.brokerageType}.transactionFees.applicationFeePerDvm`}
                                        value={
                                            props.formState[props.brokerageType]
                                                .transactionFees
                                                .applicationFeePerDvm
                                        }
                                        setFormValue={(
                                            name: string,
                                            value: string
                                        ): void => {
                                            props.setFormFieldValue(
                                                name,
                                                value
                                            );
                                            const newState = {
                                                ...props.transactionFees,
                                            };
                                            newState.applicationFeePerDvm =
                                                Number(value);
                                            props.onChange({
                                                ...newState,
                                            });
                                        }}
                                    />
                                </div>

                                <label className="label-right">
                                    per FTE DVM
                                </label>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                                {refundableField('applicationFeeRefundable')}
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center flex-hor-center aligned-md-2">
                                {creditableField('applicationFeeCreditable')}
                            </Col>
                        </Row>

                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                <div className="label-with-tooltip">
                                    <label>Monthly Engagement Fee</label>
                                    {props.brokerageType ==
                                    'fullServiceBrokeragePricing' ? (
                                        <InfoTooltip
                                            idText={`${props.brokerageType}.transactionFees.monthlyRetainerFee`}
                                            text={
                                                'An engagement fee is charged at the beginning of each month, starting at the effective date of the engagement. ' +
                                                'During any period for which the seller elects to suspend the engagement, this fee is reduce by 50% for the duration of that suspendion.'
                                            }
                                        />
                                    ) : (
                                        []
                                    )}
                                </div>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="conditional-text-center multi-field">
                                {props.brokerageType ==
                                'fullServiceBrokeragePricing' ? (
                                    <>
                                        <div className="shorter-field">
                                            <MarketplaceCurrencyField
                                                name={`${props.brokerageType}.transactionFees.monthlyRetainerFee`}
                                                value={
                                                    props.formState[
                                                        props.brokerageType
                                                    ].transactionFees
                                                        .monthlyRetainerFee
                                                }
                                                setFormValue={(
                                                    name: string,
                                                    value: string
                                                ): void => {
                                                    props.setFormFieldValue(
                                                        name,
                                                        value
                                                    );
                                                    const newState = {
                                                        ...props.transactionFees,
                                                    };
                                                    newState.monthlyRetainerFee =
                                                        Number(value);
                                                    props.onChange({
                                                        ...newState,
                                                    });
                                                }}
                                            />
                                        </div>

                                        <label className="label-inner">+</label>

                                        <div className="shorter-field">
                                            <MarketplaceCurrencyField
                                                name={`${props.brokerageType}.transactionFees.monthlyRetainerPerDvm`}
                                                value={
                                                    props.formState[
                                                        props.brokerageType
                                                    ].transactionFees
                                                        .monthlyRetainerPerDvm
                                                }
                                                setFormValue={(
                                                    name: string,
                                                    value: string
                                                ): void => {
                                                    props.setFormFieldValue(
                                                        name,
                                                        value
                                                    );
                                                    const newState = {
                                                        ...props.transactionFees,
                                                    };
                                                    newState.monthlyRetainerPerDvm =
                                                        Number(value);
                                                    props.onChange({
                                                        ...newState,
                                                    });
                                                }}
                                            />
                                        </div>

                                        <label className="label-right">
                                            per FTE DVM per month
                                        </label>
                                    </>
                                ) : (
                                    <label>NA</label>
                                )}
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className={`conditional-text-center aligned-md-2 ${
                                    props.brokerageType !=
                                    'discountBrokeragePricing'
                                        ? 'multi-field flex-hor-center'
                                        : ''
                                }`}>
                                {props.brokerageType ==
                                'discountBrokeragePricing' ? (
                                    <label>NA</label>
                                ) : (
                                    refundableField(
                                        'monthlyRetainerFeeRefundable'
                                    )
                                )}
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className={`conditional-text-center aligned-md-2 ${
                                    props.brokerageType !=
                                    'discountBrokeragePricing'
                                        ? 'flex-hor-center'
                                        : ''
                                }`}>
                                {props.brokerageType ==
                                'discountBrokeragePricing' ? (
                                    <label>NA</label>
                                ) : (
                                    creditableField(
                                        'monthlyRetainerFeeCreditable'
                                    )
                                )}
                            </Col>
                        </Row>

                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                <div className="label-with-tooltip">
                                    <label>Late Fee</label>
                                    <InfoTooltip
                                        idText={`${props.brokerageType}.transactionFees.lateFee`}
                                        text={
                                            "Late fees are charged each time the seller does not complete a step by that step's deadline."
                                        }
                                    />
                                </div>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="right-padding-5">
                                <Col className="multi-field left-padding-0 margin-bottom-half-rem">
                                    <div className="shorter-field">
                                        <MarketplaceCurrencyField
                                            name={`${props.brokerageType}.transactionFees.lateFeeFirstOccurrence`}
                                            value={
                                                props.formState[
                                                    props.brokerageType
                                                ].transactionFees
                                                    .lateFeeFirstOccurrence
                                            }
                                            setFormValue={(
                                                name: string,
                                                value: string
                                            ): void => {
                                                props.setFormFieldValue(
                                                    name,
                                                    value
                                                );
                                                const newState = {
                                                    ...props.transactionFees,
                                                };
                                                newState.lateFeeFirstOccurrence =
                                                    Number(value);
                                                props.onChange({
                                                    ...newState,
                                                });
                                            }}
                                        />
                                    </div>

                                    <label className="label-right">
                                        * FTE DVMs per 1st occurrence,
                                    </label>
                                </Col>
                                <Col className="multi-field left-padding-0">
                                    <div className="shorter-field">
                                        <MarketplaceCurrencyField
                                            name={`${props.brokerageType}.transactionFees.lateFeeAdditionalOccurrence`}
                                            value={
                                                props.formState[
                                                    props.brokerageType
                                                ].transactionFees
                                                    .lateFeeAdditionalOccurrence
                                            }
                                            setFormValue={(
                                                name: string,
                                                value: string
                                            ): void => {
                                                props.setFormFieldValue(
                                                    name,
                                                    value
                                                );
                                                const newState = {
                                                    ...props.transactionFees,
                                                };
                                                newState.lateFeeAdditionalOccurrence =
                                                    Number(value);
                                                props.onChange({
                                                    ...newState,
                                                });
                                            }}
                                        />
                                    </div>

                                    <label className="label-right">
                                        * FTE DVMs for each add. occurrence
                                        (days late)
                                    </label>
                                </Col>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                                {refundableField('lateFeeRefundable')}
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center aligned-md-2">
                                <label>No</label>
                            </Col>
                        </Row>

                        <Row className="center-items fee-item-row margin-bottom-1-rem">
                            <Col md={labelFieldWidthMd}>
                                <div className="label-with-tooltip">
                                    <label>Monthly Suspension Fee</label>
                                    <InfoTooltip
                                        idText={`${props.brokerageType}.transactionFees.suspensionFee`}
                                        text={
                                            'A suspension fee is charged for each month the engagement has been suspended by the seller. '
                                        }
                                    />
                                </div>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="conditional-text-center multi-field">
                                <>
                                    <div className="shorter-field">
                                        <MarketplaceCurrencyField
                                            name={`${props.brokerageType}.transactionFees.suspensionFee`}
                                            value={
                                                props.formState[
                                                    props.brokerageType
                                                ].transactionFees.suspensionFee
                                            }
                                            setFormValue={(
                                                name: string,
                                                value: string
                                            ): void => {
                                                props.setFormFieldValue(
                                                    name,
                                                    value
                                                );
                                                const newState = {
                                                    ...props.transactionFees,
                                                };
                                                newState.suspensionFee =
                                                    Number(value);
                                                props.onChange({
                                                    ...newState,
                                                });
                                            }}
                                        />
                                    </div>

                                    <label className="label-inner">+</label>

                                    <div className="shorter-field">
                                        <MarketplaceCurrencyField
                                            name={`${props.brokerageType}.transactionFees.suspensionFeePerDvm`}
                                            value={
                                                props.formState[
                                                    props.brokerageType
                                                ].transactionFees
                                                    .suspensionFeePerDvm
                                            }
                                            setFormValue={(
                                                name: string,
                                                value: string
                                            ): void => {
                                                props.setFormFieldValue(
                                                    name,
                                                    value
                                                );
                                                const newState = {
                                                    ...props.transactionFees,
                                                };
                                                newState.suspensionFeePerDvm =
                                                    Number(value);
                                                props.onChange({
                                                    ...newState,
                                                });
                                            }}
                                        />
                                    </div>

                                    <label className="label-right">
                                        per FTE DVM per month
                                    </label>
                                </>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className={`conditional-text-center aligned-md-2 ${
                                    props.brokerageType !=
                                    'fullServiceBrokeragePricing'
                                        ? 'multi-field flex-hor-center'
                                        : ''
                                }`}>
                                {refundableField('suspensionFeeRefundable')}
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className={`conditional-text-center aligned-md-2 ${
                                    props.brokerageType !=
                                    'fullServiceBrokeragePricing'
                                        ? 'flex-hor-center'
                                        : ''
                                }`}>
                                {creditableField('suspensionFeeCreditable')}
                            </Col>
                        </Row>

                        <Row>
                            <Col md={labelFieldWidthMd}>
                                <div className="label-with-tooltip">
                                    <label>Tempo Change Fee</label>
                                </div>
                            </Col>
                            <Col
                                md={multiFieldWidthMd}
                                className="right-padding-5 multi-field">
                                <div className="shorter-field">
                                    <MarketplaceCurrencyField
                                        name={`${props.brokerageType}.transactionFees.tempoChangeFeePerDvm`}
                                        value={
                                            props.formState[props.brokerageType]
                                                .transactionFees
                                                .tempoChangeFeePerDvm
                                        }
                                        setFormValue={(
                                            name: string,
                                            value: string
                                        ): void => {
                                            props.setFormFieldValue(
                                                name,
                                                value
                                            );
                                            const newState = {
                                                ...props.transactionFees,
                                                tempoChangeFeePerDvm:
                                                    Number(value),
                                            };
                                            props.onChange({
                                                ...newState,
                                            });
                                        }}
                                    />
                                </div>

                                <label className="label-right">
                                    * FTE DVMs
                                </label>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center multi-field flex-hor-center aligned-md-2">
                                {refundableField('tempoChangeFeeRefundable')}
                                {/* <label>NA</label> replace above or remove when Matt clarifies*/}
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center flex-hor-center aligned-md-2">
                                {creditableField('tempoChangeFeeCreditable')}
                                {/* <label>NA</label> replace above or remove when Matt clarifies*/}
                            </Col>
                        </Row>
                    </div>
                </FormGroup>

                <FormGroup>
                    <div className="fee-section">
                        <Row className="center-items fee-item-row">
                            <Col md={labelFieldWidthMd}>
                                <label>Tail Period</label>
                            </Col>
                            <Col md={multiFieldWidthMd}>
                                <div className="shorter-field">
                                    <Field
                                        type="text"
                                        name={`${props.brokerageType}.transactionFees.tailPeriodMonths`}
                                        value={
                                            (props.transactionFees.tailPeriodMonths.toString() ??
                                                '0') + ' months'
                                        }
                                        min={1}
                                        max={100}
                                        onKeyDown={(event) => {
                                            if (
                                                validateMonthsField(
                                                    event,
                                                    props.formState[
                                                        props.brokerageType
                                                    ].transactionFees
                                                        .tailPeriodMonths,
                                                    ' months'
                                                )
                                            ) {
                                                event.preventDefault();
                                            }
                                        }}
                                        onChange={(event) => {
                                            let value = event.target.value;
                                            let res = value.replace(/\D/g, '');
                                            if (!value.includes(' months')) {
                                                res = res.slice(0, -1);
                                            }
                                            const newState = {
                                                ...props.transactionFees,
                                            };
                                            newState.tailPeriodMonths =
                                                Number(res);
                                            props.onChange({
                                                ...newState,
                                            });
                                            props.setFormFieldValue(
                                                `${props.brokerageType}.transactionFees.tailPeriodMonths`,
                                                res
                                            );
                                        }}></Field>
                                </div>
                            </Col>
                            <Col
                                md={refundableFieldWidthMd}
                                className="conditional-text-center aligned-md-2">
                                <label>NA</label>
                            </Col>
                            <Col
                                md={creditableFieldWidthMd}
                                className="conditional-text-center aligned-md-2">
                                <label>NA</label>
                            </Col>
                        </Row>
                    </div>
                </FormGroup>
            </div>
        </div>
    );
};

export default TransactionFeesTable;
